import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './WhoopConnect.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const WhoopConnect = () => {
  const { user } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [userId, setUserId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);

  const linkWhoopAccount = useCallback(async (whoopUserId) => {
    if (!user?.id) return;

    try {
      const response = await fetch(
        `${API_BASE}/api/whoop/oauth/link?whoop_user_id=${whoopUserId}&internal_user_id=${user.id}`,
        { method: 'POST' }
      );

      if (response.ok) {
        console.log('Whoop account linked to internal user');
      } else {
        console.warn('Failed to link Whoop account');
      }
    } catch (err) {
      console.error('Error linking Whoop account:', err);
    }
  }, [user]);

  const checkConnectionStatus = useCallback(async (uid) => {
    try {
      const response = await fetch(`${API_BASE}/api/whoop/oauth/status?user_id=${uid}`);
      const data = await response.json();

      if (data.connected) {
        setIsConnected(true);
        setUserId(uid);
      } else {
        setIsConnected(false);
        localStorage.removeItem('whoop_user_id');
      }
    } catch (err) {
      console.error('Failed to check Whoop status:', err);
      setIsConnected(false);
    }
  }, []);

  // Check for OAuth callback and stored user ID on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const success = params.get('success');
    const returnedUserId = params.get('user_id');
    const errorParam = params.get('error');

    if (success === 'true' && returnedUserId) {
      setIsConnected(true);
      setUserId(returnedUserId);
      localStorage.setItem('whoop_user_id', returnedUserId);
      linkWhoopAccount(returnedUserId);
      window.history.replaceState({}, document.title, window.location.pathname);

      setSyncStatus({ type: 'success', message: 'Successfully connected to Whoop!' });
      setTimeout(() => setSyncStatus(null), 5000);

      // Auto-sync recovery data on first connect
      syncData(returnedUserId);
    } else if (errorParam) {
      setError(`Whoop connection failed: ${errorParam}`);
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    const storedUserId = localStorage.getItem('whoop_user_id');
    if (storedUserId) {
      checkConnectionStatus(storedUserId);
      linkWhoopAccount(storedUserId);
    }
  }, [linkWhoopAccount, checkConnectionStatus]);

  const syncData = async (uid) => {
    try {
      setSyncStatus({ type: 'info', message: 'Syncing recovery data...' });
      const response = await fetch(
        `${API_BASE}/api/whoop/oauth/sync?user_id=${uid}&days=7`,
        { method: 'POST' }
      );

      if (response.ok) {
        const data = await response.json();
        const total = (data.synced?.recovery || 0) + (data.synced?.sleep || 0);
        setSyncStatus({
          type: 'success',
          message: `Synced ${total} records from Whoop`
        });
        setTimeout(() => setSyncStatus(null), 5000);
      } else {
        setSyncStatus({ type: 'error', message: 'Failed to sync Whoop data' });
      }
    } catch (err) {
      console.error('Whoop sync error:', err);
      setSyncStatus({ type: 'error', message: 'Failed to sync Whoop data' });
    }
  };

  const handleConnect = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/api/whoop/oauth/authorize`);
      const data = await response.json();

      if (data.authorization_url) {
        window.location.href = data.authorization_url;
      } else {
        throw new Error('Failed to get authorization URL');
      }
    } catch (err) {
      setError(err.message);
      setIsLoading(false);
    }
  };

  const handleDisconnect = async () => {
    if (!userId) return;

    setIsLoading(true);
    try {
      await fetch(`${API_BASE}/api/whoop/oauth/disconnect?user_id=${userId}`, {
        method: 'DELETE'
      });

      setIsConnected(false);
      setUserId(null);
      localStorage.removeItem('whoop_user_id');

      setSyncStatus({ type: 'info', message: 'Disconnected from Whoop' });
      setTimeout(() => setSyncStatus(null), 3000);
    } catch (err) {
      console.error('Failed to disconnect:', err);
      setError('Failed to disconnect from Whoop');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="whoop-connect">
      {!isConnected ? (
        <div className="whoop-connect-section">
          <button
            className="whoop-connect-btn"
            onClick={handleConnect}
            disabled={isLoading}
          >
            <svg className="whoop-icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
            {isLoading ? 'Connecting...' : 'Connect to Whoop'}
          </button>
          <p className="whoop-note">
            Sync recovery, sleep & strain data from Whoop
          </p>
        </div>
      ) : (
        <div className="whoop-connected">
          <div className="whoop-status-row">
            <div className="whoop-status">
              <svg className="whoop-icon connected" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
              <span className="whoop-connected-text">Connected to Whoop</span>
            </div>
            <button
              className="whoop-disconnect-btn"
              onClick={handleDisconnect}
              disabled={isLoading}
            >
              Disconnect
            </button>
          </div>

          {syncStatus && (
            <div className={`whoop-sync-message ${syncStatus.type}`}>
              {syncStatus.type === 'success' && (
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
                </svg>
              )}
              {syncStatus.type === 'info' && (
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46C19.54 15.03 20 13.57 20 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74C4.46 8.97 4 10.43 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z"/>
                </svg>
              )}
              {syncStatus.type === 'error' && (
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                </svg>
              )}
              <span>{syncStatus.message}</span>
            </div>
          )}

          {!syncStatus && (
            <p className="whoop-sync-note">
              Recovery & sleep data syncs from Whoop
            </p>
          )}
        </div>
      )}

      {error && (
        <div className="whoop-error">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
          </svg>
          {error}
        </div>
      )}
    </div>
  );
};

export default WhoopConnect;
